var AbstractVideoPlayer = require("app/MediaPlayer/Tech/Abstract/AbstractVideoPlayer"),
    Loader              = require("./Loader"),
    config              = require("app/config"),
    frameThrottle       = require("enyo-perf/utils").frameThrottle,
    get                 = require("object-path").get,
    kind                = require("enyo/kind"),
    logger              = require("enyo/logger"),
    quickMacroize       = require("enyo/macroize").quickMacroize,
    themeVars           = require("app/theme/variables.js"),
    throttle            = require("enyo-perf/utils").throttle;

require("./Player.less");

module.exports = kind(/** @lends app.MediaPlayer.Tech.VimeoPlayer.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.Tech.VimeoPlayer",

    /**
     * @private
     */
    kind: AbstractVideoPlayer,

    /**
     * @private
     */
    classes: "app-media-player-vimeo-player",

    /**
     * @private
     */
    supportsSafeArea: false,

    /**
     * Vimeo Player does not support switching streams, so reuse isn't possible.
     *
     * @private
     */
    isReusable: false,

    /**
     * The dataset
     *
     * @type  {?Object}
     * @private
     */
    state: null,

    /**
     * @private
     */
    isLoadingVimeoPlayerSdk: false,

    /**
     * @type  {boolean}
     * @public
     */
    isPlaying: false,

    /**
     * The iframe node, if available.
     *
     * @type {?HTMLIFrameElement}
     * @private
     */
    iframe: null,

    /**
     * @private
     */
    autoplay: true,

    /**
     * @private
     */
    vimeoPlayer: null,

    /**
     * The ID of the Vimeo video that's currently playing.
     *
     * @private
     */
    videoId: null,

    /**
     * @readonly
     * @public
     */
    playerState: "idle",

    /**
     * Map Vimeo events to their event handlers.
     *
     * @type  {Object}
     * @private
     */
    _playerEventHandlers: {
        play:        "onPlay",
        bufferstart: "onBufferstart",
        timeupdate:  "onTimeupdate",
        pause:       "onPause",
        ended:       "onEnded",
        error:       "onError"
    },

    /**
     * @private
     */
    observers: {
        update: ["state"]
    },

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.update        = frameThrottle(this.bindSafely(this.update));
            this.updateCursor  = throttle(this.bindSafely(this.updateCursor), 100);
            this.onPlay        = this.bindSafely(this.onPlay);
            this.onBufferstart = this.bindSafely(this.onBufferstart);
            this.onTimeupdate  = this.bindSafely(this.onTimeupdate);
            this.onPause       = this.bindSafely(this.onPause);
            this.onEnded       = this.bindSafely(this.onEnded);
            this.onError       = this.bindSafely(this.onError);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    update: function() {
        var state   = this.state || {},
            videoId = get(state, "watchingSession.source", null);

        // No videoId, nothing to play yet.
        if (!videoId) {
            this.resetPlayerState();

            return;
        }

        // Only activate when absoluteShowing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("update");

            return;
        }
        this.removeFromAbsoluteShowingQueue("update");

        // Attempt to change to another video. This is explicitly not supported.
        if (this.videoId && this.videoId !== videoId) {
            this.onPlayerSetupError({
                message: "Vimeo Player SDK not support switching video sources"
            });
            this.set("isReady", -1);

            return;
        }

        // Already playing the requested video.
        if (this.videoId && this.videoId === videoId) {
            return;
        }

        // Load the Vimeo Player SDK and initialize the player.
        this.set("isLoadingVimeoPlayerSdk", true);
        Loader.load()
            .then(this.bindSafely(function() {
                this.initializePlayer();
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onPlayerSetupError({
                    message: error.message
                });
            }))
            .finally(this.bindSafely(function() {
                this.set("isLoadingVimeoPlayerSdk", false);
            }));
    },

    /**
     * @param  {boolean}  wasLoading  The previous state.
     * @private
     */
    isLoadingVimeoPlayerSdkChanged: function(wasLoading) {
        var isLoading = this.isLoadingVimeoPlayerSdk;

        if (!wasLoading && isLoading) {
            this.controller.triggerHandler("onRequestShowLoadingGraphic");
        }
        else if (wasLoading && !isLoading) {
            this.controller.triggerHandler("onRequestHideLoadingGraphic");
        }
    },

    /**
     * Resets player state and creates a new VimeoPlayer instance.
     *
     * @private
     */
    initializePlayer: function() {
        var state   = this.state || {},
            videoId = get(state, "watchingSession.source", null),
            startCursor,
            embedCode,
            tmpNode,
            iframe,
            player;

        // Ok, lets go
        this.resetPlayerState();
        this.set("isReady", 0);

        // Get videoId
        this.set("videoId", videoId);

        // Missing embed code
        if (!videoId || videoId === "") {
            this.controller.triggerHandler("onMediaPlayerError", {
                type: "missing_video_source"
            }, this);
            this.set("isReady", -1);

            return;
        }

        // Resolve starting point
        startCursor = this.resolveStartCursor();
        this.set("startCursor", startCursor);

        // Build embed code
        embedCode = quickMacroize('<iframe width="640" height="360" src="https://player.vimeo.com/video/{$videoId}?autoplay={$autoplay}&byline={$byline}&color={$color}&controls={$controls}&dnt={$dnt}&fun={$fun}&loop={$loop}&playsinline={$playsinline}&portrait={$portrait}&speed={$speed}&title={$title}&transparent={$transparent}#t={$startCursor}" frameborder="0" allow="autoplay; encrypted-media;" allowfullscreen></iframe>', {
            videoId:     videoId,
            autoplay:    0,
            byline:      0,
            color:       themeVars.RadiantMP.skinAccentColor,
            controls:    1,
            dnt:         1, // Do-not-track
            fun:         0, // Disable whimsical error messages
            loop:        0,
            playsinline: 1, // Play inline on IOS in some cases
            portrait:    0,
            speed:       0,
            title:       1,
            sidedock:    0,
            transparent: 0, // Black background enforced
            startCursor: startCursor + "s"
        });

        // Build iframe
        tmpNode           = document.createElement("div");
        tmpNode.innerHTML = embedCode;
        iframe            = tmpNode.firstElementChild;
        this.set("iframe", iframe);

        // Connect player
        player = new VimeoPlayer(iframe);
        player.ready()
            .then(this.bindSafely(function() {
                this.onPlayerReady();
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onPlayerSetupError({
                    message: error.message
                });
            }));
        this.set("vimeoPlayer", player);

        // Append iframe to node
        this.hasNode().appendChild(iframe);
    },

    /**
     * This tech is not reusable.
     *
     * @returns  {boolean}  Not reusable
     * @public
     */
    isReusableFor: function() {
        return false;
    },

    /**
     * Determines the position from which to start the video.
     *
     * @returns {number}  The position at which to start the video.
     * @private
     */
    resolveStartCursor: function() {
        var state         = this.state || {},
            sessionCursor = get(state, "watchingSession.cursor", 0),
            paramsCursor  = get(state, "params.cursor", null),
            duration      = get(state, "asset.duration", 0),
            cursor;

        cursor = paramsCursor !== null
            ? parseInt(paramsCursor, 10)
            : parseInt(sessionCursor, 10);

        return cursor >= 5 && cursor < duration - 10 ? cursor : 0;
    },

    /**
     * Get an instance of VimeoPlayer.
     *
     * @return {Object}  The VimeoPlayer instance
     */
    getVimeoPlayerInstance: function() {
        return this.get("vimeoPlayer") || null;
    },

    /**
     * Register event handlers to VimeoPlayer.
     *
     * @private
     */
    registerPlayerEventHandlers: function() {
        var map    = this._playerEventHandlers,
            player = this.getVimeoPlayerInstance(),
            event;

        for (event in map) {
            if (map.hasOwnProperty(event)) {
                player.on(event, this[map[event]]);
            }
        }
    },

    /**
     * Register event handlers to VimeoPlayer.
     *
     * @private
     */
    unregisterPlayerEventHandlers: function() {
        var map    = this._playerEventHandlers,
            player = this.getVimeoPlayerInstance(),
            event;

        for (event in map) {
            if (map.hasOwnProperty(event)) {
                player.off(event, this[map[event]]);
            }
        }
    },

    // VimeoPlayer Event Handlers
    // --------------------------------

    /**
     * Is called when the setup of the VimeoPlayer instance has failed.
     *
     *  - Resets state
     *  - Sends error event.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onPlayerSetupError: function(event) {
        this.resetPlayerState();
        this.set("isReusable", false);
        this.controller.triggerHandler("onMediaPlayerError", {
            type:    "player_setup_error",
            message: event.message
        });
    },

    /**
     * Is called by the newly created VimeoPlayer instance once it is ready.
     *
     * @private
     */
    onPlayerReady: function() {
        this.registerPlayerEventHandlers();
        this.set("isReady", 1);

        try {
            this.autoplay && this.play();
        }
        catch (error) {
            // Apparently autoplay wasn't allowed. Whatever
        }
    },

    /**
     * @private
     */
    onPlay: function() {
        // Ensure player is marked as playing.
        if (this.hasStarted) {
            this.set("isPlaying", true);
            this.set("playerState", "playing");
            this.app.$.autoplayTimerService.startTimer();
        }
    },

    /**
     * @private
     */
    onBufferstart: function() {
        this.set("playerState", "buffering");
    },

    /**
     * If the startCursor has been reached, we'll be able to say playback has
     * officially started.
     *
     * @param  {Object}  event  The event
     * @private
     */
    onTimeupdate: function(event) {
        if (!this.hasStarted && event.seconds >= this.get("startCursor")) {
            this.set("hasStarted", true);
            this.set("isPlaying", true);
            this.set("playerState", "playing");
            this.app.$.autoplayTimerService.startTimer();
        }
        this.updateCursor();
        this.set("isSeekable", true);
    },

    /**
     * Throttled cursor update sets an up-to-date cursor.
     *
     * @private
     */
    updateCursor: function() {
        this.getCursor().then(this.bindSafely(function(cursor) {
            this.isPlaying && this.set("cursor", cursor, true);
        }));
    },

    /**
     * @private
     */
    onPause: function() {
        this.set("isPlaying", false);
        this.set("playerState", "paused");
    },

    /**
     * @private
     */
    onEnded: function() {
        var state     = this.state || {},
            nextMedia = get(state, "nextMedia", null);

        if (nextMedia && get(config, "autoplay.enabled", false)) {
            this.app.$.mediaPlayerManager.autoNavToMediaItem(nextMedia);
        }
        else {
            this.app.$.autoplayTimerService.stopTimer();
        }
    },

    /**
     * VimeoPlayer encountered an error.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onError: function(event) {
        switch (event.name) {
            case "PlayInterrupted":
            case "NotAllowedError":
                // Throws an error, but player recovers just fine.
                break;
            default:
                this.controller.triggerHandler("onMediaPlayerError", {
                    type:    "player_error",
                    message: event.message
                });
                break;
        }
    },

    /**
     * Tell the autoplayTimerService there was a user interaction whenever the
     * player gets paused or unpaused.
     *
     * @param  {string}  oldState  The old state
     * @param  {string}  newState  The new state
     * @private
     */
    playerStateChanged: function(oldState, newState) {
        if (oldState !== newState && (oldState === "paused" || newState === "paused")) {
            this.app.$.autoplayTimerService.resetTimer();
        }
    },

    // VideoPlayer API
    // --------------------------------

    /**
     * Start playing
     *
     * @public
     */
    play: function() {
        var player;

        if (this.get("isReady") === 1) {
            player = this.getVimeoPlayerInstance();
            player && player.play();
        }
    },

    /**
     * Move to a certain cursor position
     *
     * @param  {number}  cursor  The cursor position to seek to
     *
     * @public
     */
    seek: function(cursor) {
        var player;

        if (this.get("isReady") === 1 && this.get("isSeekable")) {
            player = this.getVimeoPlayerInstance();
            player && player.setCurrentTime(Math.round(cursor));
        }
        else {
            this.set("startCursor", cursor);
        }
    },

    /**
     * Pause the player
     *
     * @public
     */
    pause: function() {
        var player;

        if (this.get("isReady") === 1) {
            player = this.getVimeoPlayerInstance();
            player && player.pause();
        }
    },

    /**
     * Stop the player
     *
     * @public
     */
    stop: function() {
        var player;

        if (this.get("isReady") === 1) {
            player = this.getVimeoPlayerInstance();

            // Vimeo doesn't have a stop method
            player && player.pause();
        }
    },

    /**
     * Returns the current point on the player's timeline in seconds.
     *
     * @return  {?number}  The cursor position
     *
     * @public
     */
    getCursor: function() {
        var player = this.getVimeoPlayerInstance();

        if (player && this.get("isReady") === 1) {
            return player.getCurrentTime()
                .catch(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return null;
                });
        }
        else {
            return new Promise(function(resolve) {
                resolve(null);
            });
        }
    },

    /**
     * Returns the duration of the video in seconds.
     *
     * @return  {?number}  The duration of the current asset
     *
     * @public
     */
    getLength: function() {
        var player = this.getVimeoPlayerInstance();

        if (player && this.get("isReady") === 1) {
            return player.getDuration()
                .catch(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return null;
                });
        }

        return new Promise(function(resolve) {
            resolve(null);
        });
    },

    // Destruction
    // --------------------------------

    /**
     * Destroys the current VimeoPlayer instance and resets state.
     *
     * @private
     */
    destroyPlayer: function() {
        var player = this.getVimeoPlayerInstance(),
            iframe = this.get("iframe");

        this.stop();

        if (player) {
            this.unregisterPlayerEventHandlers();
            player.destroy();
        }
        this.set("vimeoPlayer", null);

        if (iframe && iframe.parentNode) {
            iframe.remove();
        }
        this.set("iframe", null);
        this.set("videoId", null);

        this.resetPlayerState();
    },

    /**
     * Destroy the VimeoPlayer when this instance gets destroyed.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.destroyPlayer();

            // Ensures we trigger a request to hide the loading graphic.
            this.set("isLoadingVimeoPlayerSdk", false);
            sup.apply(this, arguments);
        };
    })

});
